import React, { useCallback, useEffect, useState } from 'react';
import Container from '@/components/Container';
import { Button, Typography, SelectChangeEvent } from '@mui/material';
import { BlockType } from '@/declarations/models/BlockType';
import { Page } from '@/declarations/models/Page';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/components/store/Store';
import { SiteSearchMultiSelector } from '@/components/SiteSearchMultiSelector';
import ConfusedLookingForByLooneyTunes from '@/assets/gifs/lookingFor/ConfusedLookingForByLooneyTunes.gif';
import ConfusedStephenColbert from '@/assets/gifs/lookingFor/ConfusedStephenColbert.gif';
import JoeBidenReactionLookingFor from '@/assets/gifs/lookingFor/JoeBidenReactionLookingFor.gif';
import NBALookingFor from '@/assets/gifs/lookingFor/NBALookingFor.gif';
import SquidGameLookingFor from '@/assets/gifs/lookingFor/SquidGameLookingFor.gif';
import CalculateZachGalifianakis from '@/assets/gifs/thinking-hard/CalculateZachGalifianakis.gif';
import CalculatingFigureItOut from '@/assets/gifs/thinking-hard/CalculatingFigureItOut.gif';
import ConfusedThinking from '@/assets/gifs/thinking-hard/ConfusedThinking.gif';
import BoredDailyShow from '@/assets/gifs/waiting/BoredDailyShow.gif';
import MrBeanWaiting from '@/assets/gifs/waiting/MrBeanWaiting.gif';
import MrBeanWaiting2 from '@/assets/gifs/waiting/MrBeanWaiting2.gif';
import WaitGoodbye from '@/assets/gifs/waiting/WaitGoodbye.gif';
import TheGrinchWaiting from '@/assets/gifs/waiting/TheGrinchWaiting.gif';
import ItsBeenALongTimeWaiting from '@/assets/gifs/waiting/ItsBeenALongTimeWaiting.gif';
import { Api } from '@/services/Api';
import { LoadingGif } from '../../../LoadingGif';
import { AnalysisTable } from './AnalysisTable';
import { SelectedSitesChips } from '../../../SelectedSitesChips';
import { BlockTypeSelector } from '../../../BlockTypeSelector';
import { SiteAnalysisSelector } from './SiteAnalysisSelector';

const loadingGifs = [
  ConfusedLookingForByLooneyTunes,
  ConfusedStephenColbert,
  JoeBidenReactionLookingFor,
  NBALookingFor,
  SquidGameLookingFor,
  CalculateZachGalifianakis,
  CalculatingFigureItOut,
  ConfusedThinking,
  BoredDailyShow,
  MrBeanWaiting,
  MrBeanWaiting2,
  WaitGoodbye,
  TheGrinchWaiting,
  ItsBeenALongTimeWaiting,
];

const getSanitizedUrl = (url: string) => {
  let sanitizedUrl = url.trim();
  if (!/^https?:\/\//i.test(sanitizedUrl)) {
    sanitizedUrl = `http://${sanitizedUrl.replace(/^[./]+/, '')}`;
  }
  try {
    return new URL(sanitizedUrl).href;
  } catch {
    return null;
  }
};

export const BlockTypeUsageAnalyzer = () => {
  const { t: tCommon } = useTranslation('common');
  const { t: tComp } = useTranslation('components');
  const store = useStore();
  const currentSiteId = store?.state?.selectedSite?.id || 0;

  const [pagesData, setPagesData] = useState<{ pages: Page[]; pages_count: number; sites_count: number }>({
    pages: [],
    pages_count: 0,
    sites_count: 0,
  });
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [blockType, setBlockType] = useState<BlockType | ''>('');
  const [selectedSiteIds, setSelectedSiteIds] = useState<number[]>(currentSiteId ? [currentSiteId] : []);
  const [allSites, setAllSites] = useState(false);
  const [loadingGif, setLoadingGif] = useState<string | null>(null);
  const [availableSites, setAvailableSites] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedSiteIdAnalysisOption, setSelectedSiteIdAnalysisOption] = useState<'current' | 'selective' | 'all'>(
    currentSiteId ? 'current' : 'all',
  );

  useEffect(() => {
    setSelectedSiteIds(currentSiteId ? [currentSiteId] : []);
  }, [currentSiteId]);

  useEffect(() => {
    if (loading) {
      setLoadingGif(loadingGifs[Math.floor(Math.random() * loadingGifs.length)]);
    }
  }, [loading]);

  useEffect(() => {
    Api.getAllSitesForSiteSharing()
      .fetch()
      .then((data) => {
        const sites = (data[0] || []) as Array<{ id?: number; name: string }>;
        setAvailableSites(
          sites
            .filter((site) => typeof site.id === 'number')
            .map((site) => ({ id: site.id as number, name: site.name })),
        );
      });
  }, []);

  const fetchPages = useCallback(() => {
    setLoading(true);
    setHasFetched(true);
    if (blockType !== '') {
      const request = Api.getPagesForAnalysisAndStatistics(selectedSiteIds, blockType as BlockType, allSites);
      request
        .fetchDirect([])
        .then((fetched) => {
          if (Array.isArray(fetched)) {
            setPagesData({ pages: fetched, pages_count: fetched.length, sites_count: 0 });
          } else if (fetched && 'pages' in fetched) {
            setPagesData(fetched as { pages: Page[]; pages_count: number; sites_count: number });
          } else {
            setPagesData({ pages: [], pages_count: 0, sites_count: 0 });
          }
        })
        .finally(() => setLoading(false));
      return () => request.abort();
    }
  }, [selectedSiteIds, blockType, allSites]);

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value as 'current' | 'selective' | 'all';
    setSelectedSiteIdAnalysisOption(value);
    if (value === 'current') {
      setSelectedSiteIds([currentSiteId]);
      setAllSites(false);
    } else if (value === 'selective') {
      setSelectedSiteIds([]);
      setAllSites(false);
    } else {
      setSelectedSiteIds([]);
      setAllSites(true);
    }
  };

  const handleBlockTypeChange = (event: SelectChangeEvent<string>) => {
    setBlockType(event.target.value as BlockType);
  };

  const isAnalyseDisabled =
    (['current', 'all'].includes(selectedSiteIdAnalysisOption) && blockType === '') ||
    (selectedSiteIdAnalysisOption === 'selective' && selectedSiteIds.length === 0);

  // Prepare block type options for BlockTypeSelector
  const blockTypeOptions = Object.entries(BlockType)
    .filter(
      ([key, value]) =>
        key !== 'HEADER_MENU' && !key.startsWith('FOOTER_') && value !== 'menu' && !value.startsWith('footer__'),
    )
    .map(([key, value]) => ({
      key,
      value,
      label: tCommon(`BlockType.${value}`),
    }));

  return (
    <Container left fullWidth p={2} pt={0} top column>
      <Container left fullWidth top column>
        <SiteAnalysisSelector value={selectedSiteIdAnalysisOption} onChange={handleRadioChange} />
      </Container>
      <Container left fullWidth top mt={0}>
        {selectedSiteIdAnalysisOption === 'selective' && (
          <Container left mt={2} column sx={{ width: 400, minHeight: 60 }}>
            <SiteSearchMultiSelector
              sites={availableSites.map((site) => site.name).sort()}
              selectedSites={availableSites
                .filter((site) => selectedSiteIds.includes(site.id))
                .map((site) => site.name)
                .sort()}
              onChange={(selectedNames) => {
                const ids = availableSites.filter((site) => selectedNames.includes(site.name)).map((site) => site.id);
                setSelectedSiteIds(ids);
              }}
              label='Velg sider for analyse'
              color='primary'
            />
          </Container>
        )}
        <Container left mt={2} column sx={{ width: 400, maxHeight: 60 }}>
          <BlockTypeSelector
            blockType={blockType}
            onChange={handleBlockTypeChange}
            blockTypeOptions={blockTypeOptions}
          />
        </Container>
        <Container left mt={3}>
          <Button variant='contained' fullWidth onClick={fetchPages} color='success' disabled={isAnalyseDisabled}>
            Kjør analyse
          </Button>
        </Container>
      </Container>
      {selectedSiteIdAnalysisOption === 'selective' && selectedSiteIds.length > 0 && (
        <SelectedSitesChips
          sites={availableSites.filter((site) => selectedSiteIds.includes(site.id))}
          onDelete={(id) => setSelectedSiteIds(selectedSiteIds.filter((sid) => sid !== id))}
        />
      )}

      {hasFetched &&
        (loading ? (
          <Container fullWidth pt={3} top mt={0} column sx={{ height: '80vh' }}>
            <LoadingGif src={loadingGif} />
          </Container>
        ) : (
          <Container left fullWidth pt={3} top mt={0} column>
            <AnalysisTable
              pages={pagesData.pages}
              pagesCount={pagesData.pages_count}
              sitesCount={pagesData.sites_count}
              tComp={tComp}
              getSanitizedUrl={getSanitizedUrl}
            />
          </Container>
        ))}
    </Container>
  );
};

export default BlockTypeUsageAnalyzer;
